import type { PayloadAction } from '@reduxjs/toolkit';
import {
  applyPropertyAddressToSource,
  type PropertyAddressData,
} from 'src/helper/propertyAddressBroadcast';
import type { AssigneeDetails, MetaDataObj, TagData, ValueOf } from 'src/helper/shared-types';
import {
  type InitialStateType,
  type InvoiceDataObj,
  type NormalizedResidentAvailabilityType,
  type QuoteDetailsObj,
  type ResidentInfoType,
  type TripSource,
  type WorkOrderAPIResponseObj,
  type WorkOrderDataObj,
  type WorkOrderTrips,
  workOrderInitialState,
} from 'src/redux/work-order/types';
import {
  addToListOrReorderTheList,
  type NewItemCreationEvent,
  removeDataIdFromList,
  setGroupedDataMetaLoadingReducer,
  updateDataByGroupOptions,
} from '../common/reducers';

/**
 * Merges incoming trips into the current trips state.
 * Ensures only one trip is active at a time by deactivating
 * the previous active trip (O(1)) instead of scanning all trips.
 */
const mergeTrips = (
  currentTrips: WorkOrderTrips | undefined,
  incomingTrips: TripSource[] = [],
): WorkOrderTrips => {
  const tripSource = { ...(currentTrips?.tripSource || {}) };
  const tripIds = [...(currentTrips?.trip_ids || [])];
  let activeTripId = currentTrips?.activeTripId || '';

  incomingTrips?.forEach((trip) => {
    if (!trip?.trip_id) return;

    // Merge trip data
    tripSource[trip.trip_id] = { ...(tripSource?.[trip.trip_id] || {}), ...trip };

    // Add to trip_ids if new
    if (!tripIds.includes(trip.trip_id)) {
      tripIds.unshift(trip.trip_id);
    }

    // Handle active trip swap
    if (trip.is_active && activeTripId !== trip.trip_id) {
      if (activeTripId && tripSource?.[activeTripId]) {
        tripSource[activeTripId] = { ...tripSource[activeTripId], is_active: false };
      }
      activeTripId = trip.trip_id;
    }
  });

  // If the previously active trip was deactivated by incoming data, clear it
  if (activeTripId && !tripSource?.[activeTripId]?.is_active) {
    activeTripId = '';
  }

  return { tripSource, trip_ids: tripIds, activeTripId };
};

const reducers = {
  // updateWorkOrderFilter: (
  //   state: InitialStateType,
  //   action: PayloadAction<{
  //     data: InitialStateType['appliedFilters']
  //     type: 'update' | 'reset' | 'submit' | 'clear_all';
  //   }>,
  // ) => {
  //   const { data, type } = action.payload;
  //   if (type === 'submit') {
  //     const newFilter = data as { [key: string]: string };
  //     let newValues = { ...state.appliedFilters };
  //     Object.keys(newFilter)?.forEach((item: string) => {
  //       if (!isEmpty(newFilter[item])) {
  //         newValues[item] = newFilter[item];
  //       } else {
  //         delete newValues[item];
  //       }
  //     });
  //     state.appliedFilters = newValues;
  //   }
  // },
  updateWorkOrdersByGroupOptions: (
    state: InitialStateType,
    action: PayloadAction<{
      response: { data: Array<WorkOrderDataObj>; meta: MetaDataObj };
      groupByOptionsList: Array<string>;
    }>,
  ) => {
    updateDataByGroupOptions(state, action, 'work_order_id');
  },
  clearWorkOrderData: (state: InitialStateType) => {
    state.data = workOrderInitialState?.data;
    state.groupedData = workOrderInitialState?.groupedData;
    state.groupedDataMeta = {};
  },

  /**
   * Drops the snapshot published by a successful revalidation, once the owner
   * approval form has reconciled its selections against it. Without this the
   * same delta would be re-applied on every subsequent render pass.
   */
  clearOwnerQuoteRevalidation: (state: InitialStateType) => {
    state.ownerQuote.revalidation = null;
  },

  setGroupedDataMetaLoading: setGroupedDataMetaLoadingReducer,
  shouldSidePanel: (
    state: InitialStateType,
    action: PayloadAction<InitialStateType['showSidePanel']>,
  ) => {
    state.showSidePanel = action.payload;
  },
  updateSidePanelDetails: (
    state: InitialStateType,
    action: PayloadAction<InitialStateType['showSidePanel']>,
  ) => {
    if (state.showSidePanel) {
      state.showSidePanel = { ...(state.showSidePanel || {}), ...(action.payload || {}) };
    }
    if (action.payload?.rightSection !== 'trips') {
      state.currentScheduleDetails = {
        currentStep: null,
        closeTripData: {},
        issueId: '',
        tripId: '',
      };
    }
  },

  resetSidePanelToActivityLog: (state: InitialStateType) => {
    const rightSection = state.showSidePanel?.rightSection || '';
    const mobileSection = state.showSidePanel?.mobileSection || '';
    if (!['activityLog']?.includes(rightSection) || !['activityLog']?.includes(mobileSection)) {
      state.showSidePanel = {
        ...(state.showSidePanel || {}),
        ...{
          rightSection: 'activityLog',
          mobileSection: 'activityLog',
        },
      };
    }
    state.currentScheduleDetails = {
      currentStep: null,
      closeTripData: {},
      issueId: '',
      tripId: '',
    };
  },

  updateWorkOrderDetails: (
    state: InitialStateType,
    action: PayloadAction<{
      workOrderId: string;
      data: ValueOf<WorkOrderDataObj>;
      key: string;
    }>,
  ) => {
    const currentData = { ...(state.source || {}) };

    const updatedWorkOrder = {
      ...(state.source[action?.payload?.workOrderId] || {}),
      [action?.payload?.key]: action?.payload?.data,
    };
    state.source = { ...currentData, [action?.payload?.workOrderId]: updatedWorkOrder };
  },

  updateWorkOrderNte: (
    state: InitialStateType,
    action: PayloadAction<{
      workOrderId: string;
      nteAmount: Partial<WorkOrderDataObj['nte_amount']>;
    }>,
  ) => {
    const currentData = { ...(state.source || {}) };
    const updatedWorkOrder = {
      ...(state.source[action?.payload?.workOrderId] || {}),
      nte_amount: {
        ...(state.source[action?.payload?.workOrderId]?.nte_amount || {}),
        ...action?.payload?.nteAmount,
      },
    };
    state.source = {
      ...currentData,
      [action?.payload?.workOrderId]: updatedWorkOrder,
    };
  },

  updateWorkOrderDetailsAccessMethod: (
    state: InitialStateType,
    action: PayloadAction<{
      workOrderId: string;
      access_information: any;
    }>,
  ) => {
    const currentData = { ...(state.source || {}) };
    const updatedWorkOrder = {
      ...(state.source[action?.payload?.workOrderId] || {}),
      property: {
        ...(state.source[action?.payload?.workOrderId]?.property || {}),
        access_information: action?.payload?.access_information,
      },
    };
    state.source = {
      ...currentData,
      [action?.payload?.workOrderId]: updatedWorkOrder,
    };
  },

  updateWorkOrderDetailsPropertyAddress: (
    state: InitialStateType,
    action: PayloadAction<{ workOrderId?: string; data: PropertyAddressData }>,
  ) => {
    const { workOrderId, data } = action.payload;
    state.source = applyPropertyAddressToSource(state.source, data, workOrderId);
  },

  updateWorkOrderDetailsNteAmount: (
    state: InitialStateType,
    action: PayloadAction<{
      work_order_id: string;
      min_in_cents?: number | null;
      max_in_cents?: number | null;
      default_in_cents?: number | null;
      nte_amount_in_cents?: number;
      is_ai_recommended?: boolean;
    }>,
  ) => {
    const { work_order_id: workOrderId, ...data } = action?.payload || {};
    const currentData = { ...(state?.source || {}) };
    const currentWorkOrderNteData = { ...(state?.source?.[workOrderId].nte_amount || {}) };
    const updatedWorkOrder = {
      ...(state?.source?.[workOrderId] || {}),
      nte_amount: {
        ...currentWorkOrderNteData,
        ...(data || {}),
      },
    };
    state.source = { ...currentData, [workOrderId]: updatedWorkOrder };
  },

  updateResidentInfo: (
    state: InitialStateType,
    action: PayloadAction<{
      workOrderId?: string;
      residents: Array<ResidentInfoType>;
      newDefaultResidentId: string;
    }>,
  ) => {
    const { workOrderId, residents, newDefaultResidentId } = action.payload;
    if (workOrderId) {
      const workOrder = { ...(state.source[workOrderId] || {}) };
      const updatedWorkOrder = {
        ...workOrder,
        resident_id: newDefaultResidentId,
        property: {
          ...(workOrder?.property || {}),
          residents,
        },
      };
      state.source[workOrderId] = updatedWorkOrder;
    }
  },

  updateMultipleTripData: (
    state: InitialStateType,
    action: PayloadAction<{ workOrderId: string; data: TripSource[] }>,
  ) => {
    const { workOrderId, data } = action.payload;
    if (!workOrderId || !Array.isArray(data)) return;

    state.source = {
      ...state.source,
      [workOrderId]: {
        ...(state.source[workOrderId] || {}),
        trips: mergeTrips(state.source[workOrderId]?.trips, data),
      },
    };
  },

  updateTripData: (
    state: InitialStateType,
    action: PayloadAction<{ workOrderId: string; data: TripSource }>,
  ) => {
    const { workOrderId, data } = action.payload;
    if (!workOrderId || !data?.trip_id) return;

    state.source = {
      ...state.source,
      [workOrderId]: {
        ...(state.source[workOrderId] || {}),
        trips: mergeTrips(state.source[workOrderId]?.trips, [data]),
      },
    };
  },

  toggleToRefetchWorkOrderDetails: (state: InitialStateType, action: PayloadAction<boolean>) => {
    state.showSidePanel = {
      ...(state.showSidePanel || {}),
      needToRefetch: action?.payload,
    };
  },

  setCurrentScheduleStep: (
    state: InitialStateType,
    action: PayloadAction<InitialStateType['currentScheduleDetails']>,
  ) => {
    state.currentScheduleDetails = {
      ...state.currentScheduleDetails,
      ...action?.payload,
    };
  },

  updateCloseTripData: (
    state: InitialStateType,
    action: PayloadAction<InitialStateType['currentScheduleDetails']['closeTripData']>,
  ) => {
    state.currentScheduleDetails = {
      ...state.currentScheduleDetails,
      closeTripData: {
        ...(state.currentScheduleDetails?.closeTripData || {}),
        ...action.payload,
      },
    };
  },

  updateInvoiceStep: (
    state: InitialStateType,
    action: PayloadAction<InitialStateType['currentInvoiceStep']>,
  ) => {
    state.currentInvoiceStep = {
      ...(state.currentInvoiceStep || {}),
      ...(action?.payload || {}),
    } as InitialStateType['currentInvoiceStep'];
  },

  updateInvoiceList: (
    state: InitialStateType,
    action: PayloadAction<{ workOrderId: string; data: InvoiceDataObj[] }>,
  ) => {
    const { workOrderId, data } = action.payload;
    if (state.source[workOrderId]) {
      state.source[workOrderId].invoices = {
        invoiceIds: data?.map((invoice) => invoice?.invoice_id) || [],
        invoiceSource:
          data?.reduce<Record<string, InvoiceDataObj>>((acc, invoice) => {
            acc[invoice?.invoice_id] = {
              ...(state.source[workOrderId]?.invoices?.invoiceSource?.[invoice?.invoice_id] || {}),
              ...invoice,
            };
            return acc;
          }, {}) || {},
      };
    }
  },

  removeWoIdFromList: (state: InitialStateType, action: PayloadAction<string>) => {
    removeDataIdFromList(state, action);
  },

  addToWoListOrReorderTheList: (
    state: InitialStateType,
    action: PayloadAction<{
      id: string;
      isGroupBySelected: boolean;
      data: WorkOrderAPIResponseObj;
      listKey: 'work_orders';
      pusherEvent: NewItemCreationEvent;
      keyShouldNotUpdated?: 'tags';
    }>,
  ) => {
    addToListOrReorderTheList(state, action);
  },

  updateQuoteStep: (
    state: InitialStateType,
    action: PayloadAction<Partial<InitialStateType['currentQuoteStep']>>,
  ) => {
    state.currentQuoteStep = {
      ...state.currentQuoteStep,
      ...action.payload,
    } as InitialStateType['currentQuoteStep'];
  },

  updateWorkOrderAssigneeDetails: (
    state: InitialStateType,
    action: PayloadAction<{
      workOrderId: string;
      data: AssigneeDetails;
      key: string;
      event: string;
    }>,
  ) => {
    if (action?.payload?.data?.user_id && action?.payload?.workOrderId) {
      const currentData = { ...(state.source || {}) };

      let updatedWorkOrder = {} as WorkOrderDataObj;
      if (action?.payload?.event === 'workorder.assignee.add') {
        const doesAssigneeExist = (
          state.source?.[action?.payload?.workOrderId]?.assignees || []
        )?.some((item) => item?.user_id === action?.payload?.data?.user_id);
        if (!doesAssigneeExist) {
          updatedWorkOrder = {
            ...(state.source[action?.payload?.workOrderId] || {}),
            [action?.payload?.key]: [
              ...(state.source?.[action?.payload?.workOrderId]?.assignees || []),
              action?.payload?.data,
            ],
          };
          state.source = { ...currentData, [action?.payload?.workOrderId]: updatedWorkOrder };
        }
      }
      if (action?.payload?.event === 'workorder.assignee.delete') {
        updatedWorkOrder = {
          ...(state.source?.[action?.payload?.workOrderId] || {}),
          [action?.payload?.key]: [
            ...(state.source?.[action?.payload?.workOrderId]?.assignees || []),
          ]?.filter((item) => item?.user_id !== action?.payload?.data?.user_id),
        };
        state.source = { ...currentData, [action?.payload?.workOrderId]: updatedWorkOrder };
      }
    }
  },

  updateTagDetails: (state: InitialStateType, action: PayloadAction<TagData>) => {
    if (action.payload?.tag_id) {
      const newSource = { ...(state.tagSource || {}) };
      const newTagData = [...(state.tagData?.data || [])];
      let newDataObj = { ...(newSource[action.payload?.tag_id] || {}) };
      newDataObj = {
        ...newDataObj,
        ...action.payload,
      };
      newSource[action.payload?.tag_id] = newDataObj;
      if (!newTagData?.includes(action.payload?.tag_id)) {
        state.tagData.data = [action.payload?.tag_id, ...newTagData];
      }
      state.tagSource = newSource;
    }
  },

  updateTagSource: (state: InitialStateType, action: PayloadAction<{ tagData: TagData[] }>) => {
    if (Array.isArray(action.payload?.tagData)) {
      const newSource = { ...(state.tagSource || {}) };
      action.payload?.tagData?.forEach((tag) => {
        newSource[tag.tag_id] = {
          ...newSource[tag.tag_id],
          ...tag,
        };
      });
      state.tagSource = newSource;
    }
  },

  clearTagListingData: (state: InitialStateType) => {
    state.tagSource = {};
    state.tagData = {
      data: [],
      meta: {} as MetaDataObj,
    };
  },

  deleteTagFromSource: (state: InitialStateType, action: PayloadAction<{ tagId: string }>) => {
    if (action.payload?.tagId) {
      const newSource = { ...(state.tagSource || {}) };
      const newTagData = [...(state.tagData?.data || [])];
      delete newSource?.[action.payload.tagId];
      if (newTagData?.includes(action.payload?.tagId)) {
        state.tagData.data = newTagData?.filter((id) => id !== action.payload?.tagId);
      }
      state.tagSource = newSource;
    }
  },

  updateWOAppointmentValues: (
    state: InitialStateType,
    action: PayloadAction<{
      workOrderId: string;
      tripId: string;
      data: TripSource;
    }>,
  ) => {
    const { workOrderId, data } = action.payload;
    if (!workOrderId) return;

    state.source = {
      ...state.source,
      [workOrderId]: {
        ...(state.source[workOrderId] || {}),
        trips: mergeTrips(state.source[workOrderId]?.trips, [data]),
      },
    };
  },

  /**
   * Optimistic stamp written the moment the 202 lands. The queued job has not run
   * yet, so `requested_at` here is a client clock, not server truth — record what it
   * replaced so `resolveResidentAvailabilityRequest` can undo it if the send failed.
   */
  updateResidentAvailabilityRequestedAt: (
    state: InitialStateType,
    action: PayloadAction<{
      workOrderId: string;
      serviceRequestId?: string;
      requested_at: string;
    }>,
  ) => {
    const { workOrderId, serviceRequestId, requested_at: requestedAt } = action.payload;
    const currentData = state.source[workOrderId];
    if (serviceRequestId) {
      state.pendingAvailabilityRequests = {
        ...(state.pendingAvailabilityRequests || {}),
        [serviceRequestId]: {
          workOrderId,
          previousRequestedAt: currentData?.resident_availabilities?.requested_at || '',
        },
      };
    }
    const updatedResidentAvailability = {
      ...(currentData?.resident_availabilities || {}),
      requested_at: requestedAt,
    };
    state.source[action?.payload?.workOrderId] = {
      ...currentData,
      resident_availabilities: updatedResidentAvailability,
    };
  },

  /**
   * Terminal outcome of a single-SR availability request fired from a work order,
   * driven by the `serviceRequest.requestAvailability.completed` broadcast.
   *
   * The broadcast is org-wide, so the pending-entry guard is what keeps one
   * coordinator's send from rewriting another's banner. `requestedAt` is omitted by
   * the backend whenever the server holds no timestamp (a failed or skipped send),
   * in which case the optimistic stamp is rolled back to what it replaced.
   */
  resolveResidentAvailabilityRequest: (
    state: InitialStateType,
    action: PayloadAction<{
      serviceRequestId: string;
      requestedAt?: string | null;
    }>,
  ) => {
    const { serviceRequestId, requestedAt } = action.payload;
    const pendingRequest = state.pendingAvailabilityRequests?.[serviceRequestId];
    if (!pendingRequest) return;

    delete state.pendingAvailabilityRequests[serviceRequestId];

    const currentData = state.source[pendingRequest.workOrderId];
    if (!currentData) return;

    state.source[pendingRequest.workOrderId] = {
      ...currentData,
      resident_availabilities: {
        ...(currentData?.resident_availabilities || {}),
        requested_at: requestedAt || pendingRequest.previousRequestedAt,
      },
    };
  },

  /**
   * Backstop for a request that never received its terminal broadcast (dropped
   * frame, socket reconnect). Refetching is safe here — it fires long after the
   * queued job has committed, so it can only settle the banner on server truth,
   * never race it (APX-607).
   */
  reconcileResidentAvailabilityRequest: (
    state: InitialStateType,
    action: PayloadAction<{ serviceRequestId: string }>,
  ) => {
    const { serviceRequestId } = action.payload;
    const pendingRequest = state.pendingAvailabilityRequests?.[serviceRequestId];
    if (!pendingRequest) return;

    delete state.pendingAvailabilityRequests[serviceRequestId];

    // The coordinator may have moved to another work order by now; only the open
    // panel can be refetched, and a closed panel refetches on reopen anyway.
    if (state.showSidePanel?.workOrderUuid !== pendingRequest.workOrderId) return;

    state.showSidePanel = {
      ...(state.showSidePanel || {}),
      needToRefetch: true,
    };
  },

  toggleVendorEditMode: (
    state: InitialStateType,
    action: PayloadAction<{
      vendorIds?: Array<string>;
      isEditingMode?: boolean;
    }>,
  ) => {
    if (action.payload?.isEditingMode) {
      state.vendorInEditMode = {
        isEditingMode: action.payload?.isEditingMode,
        editingVendorItemId: action.payload?.vendorIds || [],
      };
    } else {
      state.vendorInEditMode = {
        isEditingMode: false,
        editingVendorItemId: action.payload?.vendorIds || [],
      };
    }
  },

  updateTimerState: (
    state: InitialStateType,
    action: PayloadAction<{
      stopStart: boolean;
    }>,
  ) => {
    state.isTimerStop = action?.payload?.stopStart;
  },

  setFromServiceRequest: (
    state: InitialStateType,
    action: PayloadAction<{
      isFromSR: boolean;
    }>,
  ) => {
    state.isFromSR = action?.payload?.isFromSR;
  },

  switchWorkOrder: (
    state: InitialStateType,
    action: PayloadAction<{
      workOrderId: string;
      needToRefetch: boolean;
    }>,
  ) => {
    state.showSidePanel = {
      ...(state.showSidePanel || {}),
      workOrderUuid: action?.payload?.workOrderId,
      needToRefetch: action?.payload?.needToRefetch,
    };
  },
  updateAllTagList: (
    state: InitialStateType,
    action: PayloadAction<{
      data: Array<string>;
    }>,
  ) => {
    state.tagData.data = action?.payload?.data;
  },

  removeIssueFromWorkOrder: (
    state: InitialStateType,
    action: PayloadAction<{
      issueId: string;
      workOrderId: string;
    }>,
  ) => {
    const { issueId, workOrderId } = action.payload;
    if (workOrderId && state?.source?.[workOrderId]) {
      state.source[workOrderId] = {
        ...state?.source?.[workOrderId],
        work_order_issues: state?.source?.[workOrderId]?.work_order_issues?.filter(
          (id) => id !== issueId,
        ),
      };
    }
  },
  addIssuesToWorkOrder: (
    state: InitialStateType,
    action: PayloadAction<{
      issueId: string;
      workOrderId: string;
    }>,
  ) => {
    const { issueId, workOrderId } = action.payload;
    const currentIssues = state?.source?.[workOrderId]?.work_order_issues || [];
    if (!currentIssues.includes(issueId)) {
      const workOrder = state?.source?.[workOrderId];
      const scheduleDetails = workOrder?.schedule_details;
      const currentScheduleIssues = scheduleDetails?.work_order_issues || [];

      state.source[workOrderId] = {
        ...(workOrder || {}),
        work_order_issues: [...currentIssues, issueId],
        // Also update schedule_details.work_order_issues if schedule_details exists
        ...(scheduleDetails && {
          schedule_details: {
            ...scheduleDetails,
            work_order_issues: [...currentScheduleIssues, { work_order_issue_id: issueId }],
          },
        }),
      };
    }
  },
  updateIssueInWoSlice: (
    state: InitialStateType,
    action: PayloadAction<{
      workOrderId: string;
      targetWoId: string;
      issueId: string;
      type: 'assign' | 'unassign';
    }>,
  ) => {
    const { workOrderId, issueId, type, targetWoId } = action.payload;
    // add issue to work orders's service request data
    if (type === 'assign' && workOrderId && issueId) {
      state.source[workOrderId] = {
        ...(state?.source?.[workOrderId] || {}),
        service_request: {
          ...(state?.source?.[workOrderId]?.service_request || {}),
          workOrders: {
            ...(state?.source?.[workOrderId]?.service_request?.workOrders || {}),
            [targetWoId]: {
              ...(state?.source?.[workOrderId]?.service_request?.workOrders?.[targetWoId] || {}),
              issues: [
                ...(state?.source?.[workOrderId]?.service_request?.workOrders?.[targetWoId]
                  ?.issues || []),
                {
                  issue_id: issueId,
                  category: '',
                },
              ],
            },
          },
        },
      };
    }
    // Remove the issue ID from the work order matching the provided workOrderId.
    //  If the matching work order contains only one issue, remove the entire work order.
    if (type === 'unassign') {
      const currentIssues = [
        ...(state?.source?.[workOrderId]?.service_request?.workOrders?.[targetWoId]?.issues || []),
      ];
      if (currentIssues?.some((issue) => issue?.issue_id === issueId)) {
        if (currentIssues.length === 1) {
          if (state?.source?.[workOrderId]?.service_request?.workOrders?.[targetWoId]) {
            delete state.source[workOrderId].service_request.workOrders[targetWoId];
          }
          state.source[workOrderId].service_request.workOrderIds = state.source[
            workOrderId
          ].service_request?.workOrderIds?.filter((id) => id !== targetWoId);
        } else {
          state.source[workOrderId].service_request.workOrders[targetWoId].issues =
            currentIssues?.filter((issue) => issue.issue_id !== issueId);
        }
      }
    }
  },

  removeIssueFromWo: (
    state: InitialStateType,
    action: PayloadAction<{
      issueId: string;
      woId: string;
    }>,
  ) => {
    const { issueId, woId } = action.payload;
    state.source = {
      ...(state.source || {}),
      [woId]: {
        ...state.source[woId],
        work_order_issues: state?.source[woId]?.work_order_issues?.filter(
          (item) => item !== issueId,
        ),
      },
    };
  },
  deleteWorkOrder: (
    state: InitialStateType,
    action: PayloadAction<{
      woId: string;
    }>,
  ) => {
    const { woId } = action.payload;
    const newSource = { ...state.source };
    delete newSource[woId];
    state.source = newSource;
    state.data.data = state.data?.data?.filter((item) => item !== woId);
  },

  updateWoSliceKey: <K extends keyof InitialStateType>(
    state: InitialStateType,
    action: PayloadAction<{
      key: K;
      value: InitialStateType[K];
    }>,
  ) => {
    const { key, value } = action.payload;
    state[key] = value;
  },

  updateQuoteSource: (
    state: InitialStateType,
    action: PayloadAction<{
      quoteId: string;
      workOrderId: string;
      quoteData: QuoteDetailsObj;
    }>,
  ) => {
    const { quoteId, workOrderId, quoteData } = action.payload;
    state.source[workOrderId] = {
      ...(state?.source?.[workOrderId] || {}),
      quotes: {
        quoteIds: [...(state?.source?.[workOrderId]?.quotes?.quoteIds || []), quoteId],
        quoteSource: {
          ...(state?.source?.[workOrderId]?.quotes?.quoteSource || {}),
          [quoteId]: quoteData,
        },
      },
    };
  },

  addQuoteToWo: (
    state: InitialStateType,
    action: PayloadAction<{
      woId: string;
      data: QuoteDetailsObj;
    }>,
  ) => {
    const { data, woId } = action.payload;
    if (!woId || !data) return;
    const quoteId = data?.quote_id;
    const currentQuotes = state.source[woId]?.quotes || { quoteIds: [], quoteSource: {} };
    state.source[woId] = {
      ...(state.source[woId] || {}),
      quotes: {
        quoteIds: currentQuotes.quoteIds.includes(quoteId)
          ? currentQuotes.quoteIds
          : [quoteId, ...currentQuotes.quoteIds],
        quoteSource: {
          ...currentQuotes.quoteSource,
          [quoteId]: {
            ...(state?.source?.[woId]?.quotes?.quoteSource?.[quoteId] || {}),
            ...(data || {}),
          },
        },
      },
    };
  },

  updateQuoteState: (
    state: InitialStateType,
    action: PayloadAction<{
      woId: string;
      data: QuoteDetailsObj;
    }>,
  ) => {
    const { data, woId } = action.payload;
    if (!woId || !data) return;
    const quoteId = data?.quote_id;

    state.source[woId] = {
      ...(state.source[woId] || {}),
      quotes: {
        ...(state.source[woId]?.quotes || {}),
        quoteSource: {
          ...(state.source[woId]?.quotes?.quoteSource || {}),
          [quoteId]: {
            ...(state.source[woId]?.quotes?.quoteSource?.[quoteId] || {}),
            ...data,
            // if status is approved we will updated the selected option
            ...(data?.status?.value === 'approved'
              ? (() => {
                  // get current tasks
                  const tasks = state.source[woId]?.quotes?.quoteSource?.[quoteId]?.tasks || [];
                  if (Array.isArray(data?.tasks)) {
                    const updatedTasks = tasks?.map((task) => {
                      // find the matching task from the pusher response
                      const matchingDataTask = data.tasks.find(
                        (dataTask) => dataTask?.quote_task_id === task?.quote_task_id,
                      );
                      // update the selected option value
                      if (matchingDataTask) {
                        return {
                          ...task,
                          selected_option: matchingDataTask?.selected_option,
                        };
                      }

                      return task;
                    });
                    return { tasks: updatedTasks };
                  }
                  return {};
                })()
              : {}),
          },
        },
      },
    };
  },

  shouldRefetchQuote: (
    state: InitialStateType,
    action: PayloadAction<{
      woId: string;
      data: {
        quoteId: string;
        quoteEstimationRange: {
          min_in_cents: number;
          max_in_cents: number;
        };
      };
    }>,
  ) => {
    const { data, woId } = action.payload;

    if (
      (state.currentQuoteStep?.step === 'QuoteReview' ||
        state.currentQuoteStep?.step === 'EditQuote') &&
      state.currentQuoteStep?.quoteId === data?.quoteId
    ) {
      state.shouldRefetchQuote = true;
    } else {
      state.source[woId].quotes.quoteSource[data?.quoteId] = {
        ...(state.source[woId].quotes.quoteSource?.[data?.quoteId] || {}),
        quote_estimation_range: {
          min_in_cents: data?.quoteEstimationRange?.min_in_cents,
          max_in_cents: data?.quoteEstimationRange?.max_in_cents,
        },
      };
    }
  },

  // Signals that resident availability actually changed. Only dispatched by callers
  // holding both the old and new payload — an initial load has nothing to compare against
  // and must not bump, or a plain panel open would reload the schedule window.
  bumpResidentAvailabilityRevision: (
    state: InitialStateType,
    action: PayloadAction<{ workOrderId: string }>,
  ) => {
    const { workOrderId } = action.payload;
    if (!workOrderId) {
      return;
    }
    state.residentAvailabilityRevision = {
      ...(state.residentAvailabilityRevision || {}),
      [workOrderId]: (state.residentAvailabilityRevision?.[workOrderId] || 0) + 1,
    };
  },

  updateResidentAvailabilityDatesWo: (
    state: InitialStateType,
    action: PayloadAction<{ workOrderId: string; data: NormalizedResidentAvailabilityType }>,
  ) => {
    const { workOrderId, data } = action.payload;

    state.source[workOrderId].resident_availabilities.resident_availability_source = data;
    state.source[workOrderId].resident_availabilities.available_dates = Object.keys(data);
  },
};

export default reducers;
